import React, { useState, useEffect, useRef } from 'react';

const CourtPage = ({ courtNumber = 3 }) => {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const memberCacheRef = useRef({});

  const API_BASE_URL = 'https://api.asterickx.com/api/MatchScoring';
  const API_ALL_LIVE = `${API_BASE_URL}/GetAllLiveScore`;
  const API_MEMBERS = 'https://api.asterickx.com/api/Members';
  const API_MATCHES = 'https://api.asterickx.com/api/Matches';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const getFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.trim().split(' ')[0];
  };

  const fetchMatchPlayerNames = async (matchId) => {
    try {
      const response = await fetch(`${API_MATCHES}/${matchId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        console.warn(`Could not fetch match player names for ${matchId}`);
        return null;
      }
      
      const data = await response.json();
      console.log('Match player data from Matches API:', data);
      
      return {
        p1A: data.p1A || data.player1AName || data.Player1AName || "",
        p1B: data.p1B || data.player1BName || data.Player1BName || "",
        p2A: data.p2A || data.player2AName || data.Player2AName || "",
        p2B: data.p2B || data.player2BName || data.Player2BName || ""
      };
    } catch (error) {
      console.error(`Error fetching match player names ${matchId}:`, error);
      return null;
    }
  };

  const fetchMemberDetails = async (memberId) => {
    if (!memberId) {
      console.log('⚠️ No member ID provided');
      return null;
    }
    
    if (memberCacheRef.current[memberId]) {
      console.log(`✓ Using cached data for member ${memberId}`);
      return memberCacheRef.current[memberId];
    }
    
    try {
      console.log(`→ Fetching member details for ID: ${memberId}`);
      const response = await fetch(`${API_MEMBERS}/${memberId}`);
      if (!response.ok) {
        console.error(`❌ Failed to fetch member ${memberId}, status: ${response.status}`);
        return null;
      }
      const data = await response.json();
      
      memberCacheRef.current[memberId] = data;
      console.log(`✓ Fetched member ${memberId}:`, data);
      
      return data;
    } catch (error) {
      console.error(`❌ Error fetching member ${memberId}:`, error);
      return null;
    }
  };

  const fetchMatchDetails = async (matchId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${matchId}`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error(`Error fetching match ${matchId}:`, error);
      return null;
    }
  };

  const parsePlayerNames = (nameString) => {
    if (!nameString) return [];
    
    let players = [];
    
    if (nameString.includes(' & ')) {
      players = nameString.split(' & ').map(p => getFirstName(p));
    } else if (nameString.includes(' / ')) {
      players = nameString.split(' / ').map(p => getFirstName(p));
    } else if (nameString.includes('/')) {
      players = nameString.split('/').map(p => getFirstName(p));
    } else {
      players = [getFirstName(nameString)];
    }
    
    return players.filter(p => p.length > 0);
  };

  const convertApiDataToMatch = async (apiData, liveData, playerNames = null) => {
    const matchData = apiData || liveData;
    console.log('==========================================');
    console.log('=== CONVERTING MATCH DATA ===');
    console.log('Match ID:', matchData.matchId);
    console.log('Player names from Matches API:', playerNames);
    
    const sets = matchData.sets || [];
    
    let currentSetNumber = 1;
    if (liveData?.currentSetNumber) {
      currentSetNumber = liveData.currentSetNumber;
    } else {
      for (let i = 0; i < sets.length; i++) {
        if (sets[i].side1Score > 0 || sets[i].side2Score > 0) {
          currentSetNumber = i + 1;
        }
      }
    }
    
    let p1A = "", p1B = "", p2A = "", p2B = "";
    
    if (playerNames) {
      p1A = playerNames.p1A || "";
      p1B = playerNames.p1B || "";
      p2A = playerNames.p2A || "";
      p2B = playerNames.p2B || "";
    }

    const isDoubles = matchData.categoryName?.toLowerCase().includes('doubles') || 
                      (p1B && p1B.trim() !== "") || (p2B && p2B.trim() !== "");
    
    const side1Name = matchData.side1Label || 'Player 1';
    const side2Name = matchData.side2Label || 'Player 2';

    let side1Players = [];
    let side2Players = [];
    let displaySide1Name = side1Name;
    let displaySide2Name = side2Name;

    if (p1A && p1A.trim() !== "") {
      side1Players.push(getFirstName(p1A));
    }
    if (p1B && p1B.trim() !== "") {
      side1Players.push(getFirstName(p1B));
    }
    if (p2A && p2A.trim() !== "") {
      side2Players.push(getFirstName(p2A));
    }
    if (p2B && p2B.trim() !== "") {
      side2Players.push(getFirstName(p2B));
    }

    if (side1Players.length > 0) {
      displaySide1Name = side1Players.join(' & ');
    }
    if (side2Players.length > 0) {
      displaySide2Name = side2Players.join(' & ');
    }

    if (side1Players.length === 0) {
      side1Players = parsePlayerNames(side1Name);
    }
    if (side2Players.length === 0) {
      side2Players = parsePlayerNames(side2Name);
    }
    
    console.log('--- Match Details ---');
    console.log('Is Doubles:', isDoubles);
    console.log('Side 1 Players:', side1Players);
    console.log('Side 2 Players:', side2Players);
    
    const possibleSide1Fields = [
      'side1Player1MemberId', 'side1MemberId', 'player1MemberId', 
      'side1PlayerId', 'memberId1', 'side1Id'
    ];
    const possibleSide2Fields = [
      'side2Player1MemberId', 'side2MemberId', 'player2MemberId',
      'side2PlayerId', 'memberId2', 'side2Id'
    ];
    
    let side1MemberId = null;
    let side2MemberId = null;
    
    for (const field of possibleSide1Fields) {
      if (matchData[field]) {
        side1MemberId = matchData[field];
        break;
      }
    }
    
    for (const field of possibleSide2Fields) {
      if (matchData[field]) {
        side2MemberId = matchData[field];
        break;
      }
    }
    
    const side1Scores = [0, 0, 0];
    const side2Scores = [0, 0, 0];
    
    sets.forEach((set, index) => {
      if (index < 3) {
        side1Scores[index] = set.side1Score || 0;
        side2Scores[index] = set.side2Score || 0;
      }
    });

    let side1State = '';
    let side2State = '';

    console.log('--- FETCHING MEMBER STATES ---');
    
    if (side1MemberId) {
      const memberData = await fetchMemberDetails(side1MemberId);
      side1State = memberData?.state || '';
    }
    
    if (side2MemberId) {
      const memberData = await fetchMemberDetails(side2MemberId);
      side2State = memberData?.state || '';
    }
    
    console.log('=== FINAL DATA ===');
    console.log('Side 1 Players:', side1Players);
    console.log('Side 2 Players:', side2Players);
    console.log('Side 1 State:', side1State);
    console.log('Side 2 State:', side2State);
    console.log('==========================================');
    
    if (isDoubles || side1Players.length > 1 || side2Players.length > 1) {
      return {
        id: matchData.matchId || liveData.matchId,
        categoryName: matchData.categoryName,
        roundName: matchData.roundName,
        team1: {
          players: side1Players,
          state: side1State,
          scores: side1Scores,
          serving: matchData.currentServerSide === 1
        },
        team2: {
          players: side2Players,
          state: side2State,
          scores: side2Scores,
          serving: matchData.currentServerSide === 2
        },
        currentSet: currentSetNumber,
        isDoubles: true
      };
    } else {
      return {
        id: matchData.matchId || liveData.matchId,
        categoryName: matchData.categoryName,
        roundName: matchData.roundName,
        player1: {
          name: side1Players[0] || side1Name,
          state: side1State,
          scores: side1Scores,
          serving: matchData.currentServerSide === 1
        },
        player2: {
          name: side2Players[0] || side2Name,
          state: side2State,
          scores: side2Scores,
          serving: matchData.currentServerSide === 2
        },
        currentSet: currentSetNumber,
        isDoubles: false
      };
    }
  };

  const fetchCourtMatch = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      
      const response = await fetch(API_ALL_LIVE);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const allMatchesData = await response.json();
      
      console.log('=== COURT 3 DEBUG ===');
      console.log('Looking for Court:', courtNumber);
      console.log('Total matches:', allMatchesData.length);
      
      if (!Array.isArray(allMatchesData) || allMatchesData.length === 0) {
        setMatch(null);
        if (!silent) setLoading(false);
        setLastUpdate(new Date().toLocaleTimeString());
        return;
      }
      
      const courtNumberStr = String(courtNumber);
      console.log('Searching for court number:', courtNumberStr);
      
      const courtMatch = allMatchesData.find(
        m => m.matchStatus === "In Progress" && m.courtNumber === courtNumberStr
      );
      
      console.log('Found match for Court 3?', courtMatch ? 'YES' : 'NO');
      
      if (courtMatch) {
        console.log('Found court match:', courtMatch);
        const details = await fetchMatchDetails(courtMatch.matchId);
        const playerNames = await fetchMatchPlayerNames(courtMatch.matchId);
        console.log('Fetched match details:', details);
        const convertedMatch = await convertApiDataToMatch(details, courtMatch, playerNames);
        console.log('Setting match state with:', convertedMatch);
        setMatch(convertedMatch);
      } else {
        console.log('No match found for Court 3');
        setMatch(null);
      }
      
      setLastUpdate(new Date().toLocaleTimeString());
      if (!silent) setLoading(false);
    } catch (error) {
      console.error('Error fetching match:', error);
      setError(error.message);
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourtMatch();
    pollingIntervalRef.current = setInterval(() => {
      fetchCourtMatch(true);
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [courtNumber]);

  const getSetWinner = (setIndex) => {
    if (!match) return null;
    const p1Score = match.isDoubles ? match.team1.scores[setIndex] : match.player1.scores[setIndex];
    const p2Score = match.isDoubles ? match.team2.scores[setIndex] : match.player2.scores[setIndex];
    
    if (p1Score === undefined || p2Score === undefined) return null;
    if (p1Score >= 21 && p1Score - p2Score >= 2) return 1;
    if (p2Score >= 21 && p2Score - p1Score >= 2) return 2;
    if (p1Score >= 30 || p2Score >= 30) return p1Score > p2Score ? 1 : 2;
    return null;
  };

  const getSetsWon = (side) => {
    if (!match) return 0;
    let won = 0;
    for (let i = 0; i < 3; i++) {
      if (getSetWinner(i) === side) won++;
    }
    return won;
  };

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', background: '#fee', color: '#c33' }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }

  if (!match) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          textAlign: 'center',
          background: '#f5f5f5',
          padding: '60px',
          borderRadius: '12px',
          border: '2px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>🏸</div>
          <h2 style={{ color: '#333', marginBottom: '10px', fontSize: '28px' }}>Court {courtNumber}</h2>
          <p style={{ color: '#666', fontSize: '18px' }}>
            {loading && !lastUpdate ? 'Loading...' : 'No Live Match'}
          </p>
        </div>
      </div>
    );
  }

  const side1 = match.isDoubles ? match.team1 : match.player1;
  const side2 = match.isDoubles ? match.team2 : match.player2;
  const side1Name = match.isDoubles ? side1.players.join(' & ') : side1.name;
  const side2Name = match.isDoubles ? side2.players.join(' & ') : side2.name;

  console.log('Rendering with match:', match);
  console.log('Side 1 state to display:', side1.state);
  console.log('Side 2 state to display:', side2.state);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      padding: '30px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        width: 'fit-content',
        maxWidth: '700px'
      }}>
        <div style={{
          background: '#f5f5f5',
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '2px solid #e0e0e0'
        }}>
          {/* Player 1 Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '50px 280px repeat(3, 50px)',
            alignItems: 'stretch',
            borderBottom: '2px solid #d0d0d0'
          }}>
            <div style={{
              background: '#e8e8e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px'
            }}>
              <div style={{ fontSize: '24px' }}>🏸</div>
            </div>

            <div style={{
              padding: '10px 15px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              background: '#ffffff',
              gap: '2px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {side1Name}
                </span>
                {side1.serving && (
                  <div style={{
                    background: '#ffd700',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    flexShrink: 0
                  }} />
                )}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                fontWeight: '500'
              }}>
                {/* {side1.state || 'Loading...'} */}
              </div>
            </div>

            {[0, 1, 2].map((setIndex) => {
              const score = side1.scores[setIndex];
              const isCurrentSet = match.currentSet === setIndex + 1;
              const winner = getSetWinner(setIndex);
              
              return (
                <div
                  key={setIndex}
                  style={{
                    background: isCurrentSet ? '#ffd700' : winner === 1 ? '#4caf50' : '#e8e8e8',
                    color: isCurrentSet || winner === 1 ? (isCurrentSet ? '#000' : '#fff') : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                    fontWeight: 'bold',
                    padding: '10px 0',
                    borderBottom: '1px solid #d0d0d0'
                  }}
                >
                  {score > 0 ? score : '-'}
                </div>
              );
            })}
          </div>

          {/* Player 2 Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '50px 280px repeat(3, 50px)',
            alignItems: 'stretch'
          }}>
            <div style={{
              background: '#e8e8e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px'
            }}>
              <div style={{ fontSize: '24px' }}>🏸</div>
            </div>

            <div style={{
              padding: '10px 15px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              background: '#ffffff',
              gap: '2px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {side2Name}
                </span>
                {side2.serving && (
                  <div style={{
                    background: '#ffd700',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    flexShrink: 0
                  }} />
                )}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                fontWeight: '500'
              }}>
                {/* {side2.state || 'Loading...'} */}
              </div>
            </div>

            {[0, 1, 2].map((setIndex) => {
              const score = side2.scores[setIndex];
              const isCurrentSet = match.currentSet === setIndex + 1;
              const winner = getSetWinner(setIndex);
              
              return (
                <div
                  key={setIndex}
                  style={{
                    background: isCurrentSet ? '#ffd700' : winner === 2 ? '#4caf50' : '#e8e8e8',
                    color: isCurrentSet || winner === 2 ? (isCurrentSet ? '#000' : '#fff') : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                    fontWeight: 'bold',
                    padding: '10px 0'
                  }}
                >
                  {score > 0 ? score : '-'}
                </div>
              );
            })}
          </div>
        </div>

        {/* Match Info Below */}
        <div style={{
          marginTop: '15px',
          padding: '10px 15px',
          background: '#f5f5f5',
          borderRadius: '6px',
          color: '#666',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: '1px solid #e0e0e0'
        }}>
          <div>
            <strong style={{ color: '#1a1a1a' }}>Court {courtNumber}</strong> • {match.categoryName} • {match.roundName}
          </div>
          <div>
            Set {match.currentSet} • {getSetsWon(1)}-{getSetsWon(2)}
            {lastUpdate && <span style={{ marginLeft: '10px' }}>• {lastUpdate}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default () => <CourtPage courtNumber={3} />;